import type {
  CatalogMenuItem,
  CatalogSpecialMenuItem
} from '@store/CatalogStore.type';
import { observer } from 'mobx-react';
import type { FC, MouseEventHandler, SyntheticEvent } from 'react';
import { useMemo, useEffect, useRef, useState, useCallback } from 'react';

import { Link } from '@compat/router';
import { LazyLoadImage } from '@components/reactLazyLoadImageComponent';
import {
  CatalogMenuItemComp,
  MenuList,
  MenuListBarMenu
} from './components/components';
import { useRubricBannersQuery } from '@queries/index';
import type { CatalogTypesModel } from '@schema/cart';
import { twcls } from '@utils/twcls';
import RubricBanner from '@components/Carousel/RubricBanners/RubricBanner';
import LoadingSkeleton from '@components/LoadingSkeleton';
import clsx from 'clsx';
import { weeklyPromosBanners } from '@components/LandingBlocks/OysterBarTest/sections/WeeklyPromos';
import { Picture } from '@ui';

interface CatalogBtnListProps {
  activeMenu: CatalogMenuItem;
  catalogList: CatalogMenuItem[];
  catalogSpecialMenu: CatalogSpecialMenuItem[];
  setActiveMenu: (item: CatalogMenuItem) => void;
  hide: () => void;
  onCatalogItemClick: (e: SyntheticEvent) => void;
}

let last5numbers: number[] = [];
const BANNERS_COUNT = 5;

function shuffle(array: number[]): number[] {
  let currentIndex = array.length;
  let randomIndex;

  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex]
    ];
  }

  return array;
}

function generateRandomNumbers(arrLength: number): number[] {
  const all = new Set(Array.from(Array(arrLength).keys()));

  for (const used of last5numbers) {
    all.delete(used);
  }

  const shuffled = shuffle(Array.from(all));
  last5numbers = shuffled.slice(0, BANNERS_COUNT);

  return last5numbers;
}

const CatalogBtnList: FC<CatalogBtnListProps> = ({
  activeMenu,
  catalogSpecialMenu,
  catalogList,
  setActiveMenu,
  hide,
  onCatalogItemClick
}) => {
  const currentTitleRef = useRef(activeMenu.title ?? '');
  const [updated, setUpdated] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState<CatalogTypesModel | null>(
    null
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // При смене activeMenu.identify меняется queryKey — данные для новой
  // рубрики подгружаются сами, а уже посещённые рубрики отдаются из кэша
  const { data: rubricBannersData, isPending: isRubricBannersLoading } =
    useRubricBannersQuery({ type: activeMenu.identify });

  const rubricBanners = useMemo(
    () => rubricBannersData?.getRubricBanners ?? [],
    [rubricBannersData]
  );

  useEffect(() => {
    setActiveSubMenu(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [activeMenu.identify]);

  const onMouseEnterMenu = useCallback(
    (item: CatalogMenuItem) => {
      if (currentTitleRef.current === item.identify) {
        return;
      }
      setActiveMenu(item);
      setUpdated(true);
      timeoutRef.current = setTimeout(() => {
        setUpdated(false);
        currentTitleRef.current = item.identify ?? '';
      }, 300);
    },
    [setActiveMenu]
  );

  const specialMenuListRandomed = useMemo(
    () => {
      const randomNumbers = generateRandomNumbers(catalogSpecialMenu.length);
      return catalogSpecialMenu.filter((item, index) =>
        randomNumbers.includes(index)
      );
    },
    // чтобы когда изменялся activeMenu.title - менялся список баннеров
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalogSpecialMenu.length, activeMenu.title]
  );

  const promosInMenu = useMemo(
    () =>
      activeMenu.childrenItems?.length
        ? activeMenu.childrenItems.filter(item => item?.id === '-1')
        : [],
    [activeMenu.childrenItems]
  );

  const menuWithoutPromos = useMemo(
    () =>
      activeMenu.childrenItems?.length
        ? activeMenu.childrenItems.filter(item => item?.id !== '-1')
        : [],
    [activeMenu.childrenItems]
  );

  const menuWithPromosHalfIndex = useMemo(
    () => Math.ceil((activeMenu.childrenItems?.length || 0) / 2),
    [activeMenu.childrenItems?.length]
  );
  const leftSubMenu = useMemo(
    () => menuWithoutPromos.slice(0, menuWithPromosHalfIndex),
    [menuWithoutPromos, menuWithPromosHalfIndex]
  );
  const rightSubMenu = useMemo(
    () => [
      ...menuWithoutPromos.slice(menuWithPromosHalfIndex),
      ...promosInMenu
    ],
    [promosInMenu, menuWithPromosHalfIndex, menuWithoutPromos]
  );
  const onTabClick: MouseEventHandler = useCallback(
    e => {
      onCatalogItemClick(e);
      hide();
    },
    [onCatalogItemClick, hide]
  );
  const banners = useMemo(
    () =>
      activeMenu?.isBarMenu
        ? weeklyPromosBanners.map(item => (
            <li
              key={item.id}
              className='w-full h-[140px] rounded-xl overflow-hidden'
            >
              <Picture
                icon={item.image}
                className='object-cover w-full h-full'
              />
            </li>
          ))
        : !!rubricBanners?.length &&
          rubricBanners
            .slice(0, 5)
            .map((item, index) => (
              <RubricBanner
                key={item.id}
                onClick={hide}
                index={index}
                banner={item}
                visibleByDefault
                className='min-h-[8.25rem] max-h-[8.25rem]'
              />
            )),
    [activeMenu?.isBarMenu, hide, rubricBanners]
  );
  return (
    <div className='flex h-full w-[87.5rem] justify-between py-8 lg:pl-1.5 lg:1.5 bg-white rounded-b-4xl px-[0.563rem]'>
      <div className='mr-7.5'>
        <ul className='max-h-full pr-2 overflow-auto w-72 custom-scroll custom-scroll-mini'>
          {!!catalogList?.length &&
            catalogList?.map((item, index) => (
              <CatalogMenuItemComp
                key={item.id}
                isPrevItemActive={
                  catalogList?.[index - 1]?.identify === activeMenu?.identify ||
                  false
                }
                isNextItemActive={
                  catalogList?.[index + 1]?.identify === activeMenu?.identify ||
                  false
                }
                activeIdentify={activeMenu?.identify ?? ''}
                item={item}
                setActiveMenu={onMouseEnterMenu.bind(null, item)}
                onClick={onTabClick}
                activeMenu={activeMenu}
              />
            ))}
        </ul>
      </div>
      <div
        className={`relative mr-15 flex-1 h-full flex flex-col ${
          updated ? 'animate-leftToRight' : ''
        }`}
      >
        {/* <div className={'relative mr-15 flex-1 h-full flex flex-col'}> */}
        {!!activeMenu.rubricGroup?.length && (
          <>
            <ul className={'flex flex-1 flex-wrap items-center mb-3 text-sm '}>
              {activeMenu.rubricGroup.map(item =>
                item.title === 'Все' ? null : (
                  <li key={item.identify} className='flex mb-2 mr-2'>
                    <Link
                      onClick={hide}
                      to={item.url}
                      className={twcls(
                        `px-3 py-2 bg-[var(--light-grey)] rounded-lg cursor-pointer transition-colors hover:bg-[var(--dark-red)] hover:text-white`
                      )}
                    >
                      {item.title}
                    </Link>
                  </li>
                )
              )}
            </ul>
            <div className='w-full h-px bg-[#EBEBEB] mb-5' />
          </>
        )}
        <div className='flex h-full max-h-full overflow-hidden'>
          {activeMenu?.isBarMenu ? (
            <div
              className={clsx(
                'flex-1 overflow-hidden',
                updated && 'animate-leftToRight'
              )}
            >
              <MenuListBarMenu
                list={menuWithoutPromos}
                activeMenu={activeMenu}
                activeSubMenu={activeSubMenu}
                onClick={onCatalogItemClick}
              />
            </div>
          ) : (
            <>
              <div
                className={clsx(
                  'flex-1 mr-10 overflow-hidden',
                  updated && 'animate-leftToRight'
                )}
              >
                {!!activeMenu.childrenItems?.length && (
                  <MenuList
                    list={leftSubMenu}
                    activeMenu={activeMenu}
                    activeSubMenu={activeSubMenu}
                    onClick={onCatalogItemClick}
                  />
                )}
              </div>
              <div
                className={clsx(
                  'flex-1 mr-10 overflow-hidden',
                  updated && 'animate-leftToRight'
                )}
              >
                {!!activeMenu.childrenItems?.length && (
                  <MenuList
                    list={rightSubMenu}
                    activeMenu={activeMenu}
                    activeSubMenu={activeSubMenu}
                    onClick={onCatalogItemClick}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <div
        className={`h-full w-80 space-y-3.5 max-h-full overflow-auto custom-scroll custom-scroll-mini pr-2  ${
          updated ? ' animate-leftToRight' : ''
        }`}
      >
        {isRubricBannersLoading &&
          Array.from({ length: 5 }, (_, index) => index).map(item => (
            <LoadingSkeleton
              key={item}
              style={{
                width: '100%',
                height: '8.25rem',
                minHeight: '8.25rem',
                borderRadius: '0.75rem'
              }}
            />
          ))}
        {!isRubricBannersLoading && banners}
        {!activeMenu?.isBarMenu &&
          !isRubricBannersLoading &&
          specialMenuListRandomed
            .slice(0, rubricBanners?.length < 5 ? 5 - rubricBanners?.length : 0)
            .map(item => (
              <Link
                key={item.id}
                onClick={hide}
                to={item.link ?? ''}
                className='relative block w-full h-full p-4 overflow-hidden cursor-pointer rounded-xl  min-h-[8.25rem] max-h-[8.25rem]'
              >
                <span
                  className='relative z-10 font-medium'
                  style={
                    item.title_color
                      ? {
                          color: item.title_color
                        }
                      : undefined
                  }
                  dangerouslySetInnerHTML={{
                    __html: item.title ?? ''
                  }}
                />
                {item.image && (
                  <LazyLoadImage
                    className='absolute top-0 left-0 object-cover w-full h-full'
                    src={item.image}
                    alt={item?.title?.replace('<br/>', '') ?? 'Изображение'}
                  />
                )}
              </Link>
            ))}
      </div>
    </div>
  );
};

export default observer(CatalogBtnList);
